import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import SupportModal from '../Support/SupportModal';
import { FaChevronDown, FaChevronRight } from 'react-icons/fa';
import {
    AiOutlineHome,
    AiOutlineShoppingCart,
    AiOutlineTag,
    AiOutlineUser,
    AiOutlineBarChart,
    AiOutlineBulb,
    AiOutlineShop,
    AiOutlineSetting,
    AiOutlineLogout,
    AiOutlineGlobal,
    AiOutlineTeam,
    AiOutlineCreditCard,
    AiOutlineDollar,
    AiOutlineFileText,
    AiOutlineShopping,
    AiOutlineVideoCamera,
    AiOutlineMail,
    AiOutlineDatabase,
    AiOutlineDashboard,
    AiOutlineLineChart,
    AiOutlineSearch,
    AiOutlineMessage,
    AiOutlineCustomerService,
    AiOutlinePhone,
    AiOutlineRollback,
} from 'react-icons/ai';
import { FaBox, FaUsers, FaStore, FaFileInvoice, FaUserPlus } from 'react-icons/fa';
import { MdColorLens, MdDns } from 'react-icons/md';
import styles from './Sidebar.module.css';
import myAxios from '../AxiosUrl';

function Sidebar() {
    const location = useLocation();
    const navigate = useNavigate();
    const sidebarRef = useRef(null);
    const [store, setStore] = useState();
    const [userRole, setUserRole] = useState(null);
    // "Contact Support" form (2026-11-04: moved here from the floating button)
    const [supportOpen, setSupportOpen] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        products: false,
        analytics: false,
        sales: false,
        blog: false,
        marketing: false,
        seo: false,
        email: false,
        apps: false,
        superadmin: false,
    });

    const toggleSection = (section) => {
        setExpandedSections((prev) => {
            // Close all other sections when opening a new one
            const newState = {
                products: false,
                analytics: false,
                sales: false,
                blog: false,
                marketing: false,
                seo: false,
                email: false,
                apps: false,
                superadmin: false,
            };
            // Toggle the clicked section
            newState[section] = !prev[section];
            return newState;
        });
    };

    const closeAllDropdowns = () => {
        setExpandedSections({
            products: false,
            analytics: false,
            sales: false,
            blog: false,
            marketing: false,
            seo: false,
            email: false,
            apps: false,
            superadmin: false,
        });
    };

    // Click outside handler to close dropdowns
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
                // Close all dropdowns when clicking outside
                setExpandedSections((prev) => {
                    // Only update if any section is expanded
                    const hasExpanded = Object.values(prev).some(val => val === true);
                    if (!hasExpanded) return prev;

                    return {
                        products: false,
                        analytics: false,
                        sales: false,
                        blog: false,
                        marketing: false,
                        seo: false,
                        email: false,
                        apps: false,
                        superadmin: false,
                    };
                });
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        const uid = localStorage.getItem('id');
        const role = localStorage.getItem('role');
        setUserRole(parseInt(role));

        if (uid) {
            myAxios
                .post(`/store/${uid}`)
                .then((res) => {
                    setStore(res.data);
                })
                .catch((err) => {
                    console.log('Error fetching store data:', err);
                });
        }
    }, []);

    const logoutHandler = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('id');
        localStorage.removeItem('IsSuper');
        localStorage.removeItem('IsTeam');
        localStorage.removeItem('role');
        localStorage.removeItem('CreateStore');
        navigate('/');
    };

    const navigationItems = [
        {
            id: 'home',
            label: 'Home',
            icon: AiOutlineHome,
            path: '/dashboard',
            badge: null,
        },
        {
            id: 'orders',
            label: 'Orders',
            icon: AiOutlineShoppingCart,
            path: '/order-list',
            badge: null,
        },
        {
            id: 'returns',
            label: 'Returns',
            icon: AiOutlineRollback,
            path: '/returns',
        },
        {
            id: 'products',
            label: 'Products',
            icon: AiOutlineTag,
            path: '/product',
            children: [
                { label: 'All products', path: '/product' },
                { label: 'Add product', path: '/add-product' },
                { label: 'Category', path: '/category' },
                { label: 'Special category', path: '/special-category' },
            ],
        },
        {
            id: 'brand',
            label: 'Brand',
            icon: AiOutlineTag,
            path: '/brand',
        },
        {
            id: 'customers',
            label: 'Customers',
            icon: AiOutlineUser,
            path: '/customers',
        },
        {
            id: 'ai-calling',
            label: 'AI Calling',
            icon: AiOutlinePhone,
            path: '/ai-calling',
        },
        {
            id: 'analytics',
            label: 'Analytics',
            icon: AiOutlineBarChart,
            path: '/advance-report',
            children: [
                { label: 'Product Sales', path: '/advance-report' },
                { label: 'Customer Analytics', path: '/customer-analytics' },
                { label: 'Meta Analytics', path: '/meta-analytics' },
                { label: 'Messenger & WhatsApp', path: '/chat-automation' },
                { label: 'Claude AI (MCP)', path: '/claude-mcp' },
            ],
        },
        // {
        //     id: 'expense',
        //     label: 'Expense',
        //     icon: AiOutlineDollar,
        //     path: '/expense',
        // },
        // {
        //     id: 'accounts',
        //     label: 'Accounts',
        //     icon: AiOutlineFileText,
        //     path: '/accounts',
        // },
        // {
        //     id: 'purchase',
        //     label: 'Purchase',
        //     icon: AiOutlineShopping,
        //     path: '/purchase',
        // },
        // {
        //     id: 'visitor-video',
        //     label: 'Visitor Video',
        //     icon: AiOutlineVideoCamera,
        //     path: '/visitor-video',
        // },
        {
            id: 'contact-messages',
            label: 'Messages',
            icon: AiOutlineMessage,
            path: '/contact-messages',
        },
        {
            id: 'email',
            label: 'BusinessEmail',
            icon: AiOutlineMail,
            path: '/email-management',
            children: [
                { label: 'Email Management', path: '/email-management' },
                { label: 'Email Inbox', path: '/email-inbox' },
            ],
        },
        // {
        //     id: 'buy-domain',
        //     label: 'Buy Domain',
        //     icon: AiOutlineGlobal,
        //     path: '/buy-domain',
        // },
        {
            id: 'blog',
            label: 'Blog Post',
            icon: AiOutlineFileText,
            path: '/blog-list',
            children: [
                { label: 'All Posts', path: '/blog-list' },
                { label: 'New Post', path: '/blog/add' },
            ],
        },
        {
            id: 'marketing',
            label: 'Marketing',
            icon: AiOutlineBulb,
            path: '/banner-list',
            children: [
                { label: 'Banners', path: '/banner-list' },
                { label: 'Landing pages', path: '/landing-list' },
            ],
        },
        {
            id: 'seo',
            label: 'SEO Management',
            icon: AiOutlineSearch,
            path: '/seo/faq',
            children: [
                { label: 'Home Page SEO', path: '/seo/home-seo' },
                { label: 'FAQ Management', path: '/seo/faq' },
                { label: 'Review Management', path: '/seo/reviews' },
                { label: 'SEO Settings', path: '/seo/settings' },
            ],
        },
    ];

    const salesChannels = [
        { label: 'Online Store', icon: AiOutlineShop, path: '/new-store' },
        { label: 'DNS', icon: MdDns, path: '/dns-list' },
        { label: 'Templates', icon: MdColorLens, path: '/template' },
        { label: 'Theme', icon: MdColorLens, path: '/theme' },
        { label: 'Team', icon: AiOutlineTeam, path: '/team' },
        { label: 'Billing', icon: AiOutlineCreditCard, path: '/billing' },
    ];

    // Super Admin Menu Items (only visible for role = 1)
    const superAdminItems = [
        { label: 'Dashboard', icon: AiOutlineDashboard, path: '/superadmin' },
        { label: 'Stores', icon: FaStore, path: '/superadmin/stores' },
        { label: 'Users', icon: FaUsers, path: '/superadmin/users' },
        { label: 'Subscription Info', icon: FaFileInvoice, path: '/superadmin/user-subscription-info' },
        { label: 'Manual Subscription', icon: FaUserPlus, path: '/superadmin/manual-subscription' },
        { label: 'Packages', icon: FaBox, path: '/superadmin/packages' },
        { label: 'Billing', icon: FaFileInvoice, path: '/superadmin/billing' },
        { label: 'Analytics', icon: AiOutlineLineChart, path: '/superadmin/analytics' },
        { label: 'DNS Config', icon: MdDns, path: '/superadmin/dns-config' },
        { label: 'DNS Records', icon: AiOutlineDatabase, path: '/superadmin/dns-records' },
        { label: 'Email Management', icon: AiOutlineMail, path: '/superadmin/email-management' },
        { label: 'Email Inbox', icon: AiOutlineMail, path: '/superadmin/email-inbox' },
        { label: 'SMS Marketing', icon: AiOutlineMessage, path: '/superadmin/sms-marketing' },
        { label: 'Call Numbers', icon: AiOutlinePhone, path: '/superadmin/call-numbers' },
        { label: 'Support Tickets', icon: AiOutlineCustomerService, path: '/superadmin/support-tickets' },
        { label: 'Logs', icon: AiOutlineFileText, path: '/superadmin/logs' },
    ];

    const isActive = (path) => {
        return location.pathname === path;
    };

    const isParentActive = (children) => {
        if (!children) return false;
        return children.some((child) => location.pathname === child.path);
    };

    return (
        <div className={styles.sidebar} ref={sidebarRef}>
            {/* Logo Section */}
            <div className={styles.logoSection}>
                {userRole === 1 ? (
                    <div className={styles.logo}>
                        <span className={styles.superAdminLogoText}>SUPER ADMIN</span>
                    </div>
                ) : (
                    <Link
                        to="/dashboard"
                        className={styles.logoLink}
                        onClick={closeAllDropdowns}
                    >
                        <div className={styles.logo}>
                            <AiOutlineShop className={styles.logoIcon} />
                            <span className={styles.logoText}>
                                {store?.domain ? store.domain : 'Admin'}
                            </span>
                        </div>
                    </Link>
                )}
            </div>

            {/* Main Navigation - Hidden for Super Admin (role = 1) */}
            {userRole !== 1 && (
                <nav className={styles.navigation}>
                    {navigationItems.map((item) => (
                        <div key={item.id} className={styles.navItem}>
                            {item.children ? (
                                <div className={styles.navGroup}>
                                    <button
                                        className={`${styles.navButton} ${
                                            isParentActive(item.children)
                                                ? styles.active
                                                : ''
                                        }`}
                                        onClick={() => toggleSection(item.id)}
                                    >
                                        <item.icon className={styles.navIcon} />
                                        <span className={styles.navLabel}>
                                            {item.label}
                                        </span>
                                        {expandedSections[item.id] ? (
                                            <FaChevronDown
                                                className={styles.chevron}
                                            />
                                        ) : (
                                            <FaChevronRight
                                                className={styles.chevron}
                                            />
                                        )}
                                    </button>
                                    {expandedSections[item.id] && (
                                        <div className={styles.navChildren}>
                                            {item.children.map((child, index) => (
                                                <Link
                                                    key={index}
                                                    to={child.path}
                                                    className={`${
                                                        styles.navChild
                                                    } ${
                                                        isActive(child.path)
                                                            ? styles.active
                                                            : ''
                                                    }`}
                                                    onClick={closeAllDropdowns}
                                                >
                                                    {child.label}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Link
                                    to={item.path}
                                    className={`${styles.navButton} ${
                                        isActive(item.path) ? styles.active : ''
                                    }`}
                                    onClick={closeAllDropdowns}
                                >
                                    <item.icon className={styles.navIcon} />
                                    <span className={styles.navLabel}>
                                        {item.label}
                                    </span>
                                    {item.badge && (
                                        <span className={styles.badge}>
                                            {item.badge}
                                        </span>
                                    )}
                                </Link>
                            )}
                        </div>
                    ))}
                </nav>
            )}

            {/* Store Settings Section - Hidden for Super Admin (role = 1) */}
            {userRole !== 1 && (
                <div className={styles.salesChannels}>
                    <div className={styles.sectionHeader}>
                        <span className={styles.sectionTitle}>STORE SETTINGS</span>
                    </div>
                    <div className={styles.channelsList}>
                        {salesChannels.map((channel, index) => (
                            <Link
                                key={index}
                                to={channel.path}
                                className={`${styles.channelItem} ${
                                    isActive(channel.path)
                                        ? styles.channelActive
                                        : ''
                                }`}
                                onClick={closeAllDropdowns}
                            >
                                <channel.icon className={styles.channelIcon} />
                                <span className={styles.channelLabel}>
                                    {channel.label}
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}


            {/* Super Admin Section - Only visible for role = 1 */}
            {userRole === 1 && (
                <div className={styles.superAdminMenuSection}>
                    <div className={styles.channelsList}>
                        {superAdminItems.map((item, index) => (
                            <Link
                                key={index}
                                to={item.path}
                                className={`${styles.channelItem} ${
                                    isActive(item.path)
                                        ? styles.channelActive
                                        : ''
                                }`}
                                onClick={closeAllDropdowns}
                            >
                                <item.icon className={styles.channelIcon} />
                                <span className={styles.channelLabel}>
                                    {item.label}
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Settings - Regular Users */}
            {userRole !== 1 && (
                <div className={styles.settings}>
                    <Link
                        to="/settings"
                        className={`${styles.navButton} ${
                            isActive('/settings') ? styles.active : ''
                        }`}
                        onClick={closeAllDropdowns}
                    >
                        <AiOutlineSetting className={styles.navIcon} />
                        <span className={styles.navLabel}>Settings</span>
                    </Link>
                    <Link
                        to="/policy"
                        className={`${styles.navButton} ${
                            isActive('/policy') ? styles.active : ''
                        }`}
                        onClick={closeAllDropdowns}
                    >
                        <AiOutlineFileText className={styles.navIcon} />
                        <span className={styles.navLabel}>Policy</span>
                    </Link>
                    <button
                        type="button"
                        className={styles.navButton}
                        onClick={() => {
                            closeAllDropdowns();
                            setSupportOpen(true);
                        }}
                    >
                        <AiOutlineCustomerService className={styles.navIcon} />
                        <span className={styles.navLabel}>Support</span>
                    </button>
                </div>
            )}
            {supportOpen && createPortal(<SupportModal onClose={() => setSupportOpen(false)} />, document.body)}

            {/* Settings - Super Admin (role = 1) */}
            {userRole === 1 && (
                <div className={styles.superAdminBottomSection}>
                    <Link
                        to="/superadmin/settings"
                        className={`${styles.navButton} ${
                            isActive('/superadmin/settings') ? styles.active : ''
                        }`}
                        onClick={closeAllDropdowns}
                    >
                        <AiOutlineSetting className={styles.navIcon} />
                        <span className={styles.navLabel}>Settings</span>
                    </Link>
                </div>
            )}

            {/* Logout */}
            <div className={styles.logout}>
                <button
                    onClick={logoutHandler}
                    className={`${styles.navButton} ${styles.logoutButton}`}
                >
                    <AiOutlineLogout className={styles.navIcon} />
                    <span className={styles.navLabel}>Logout</span>
                </button>
            </div>
        </div>
    );
}

export default Sidebar;
